import React, { useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, useInView } from 'framer-motion';

const Section = styled.section`
  padding: 8rem 0;
`;

const Container = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 0 1.25rem;
  }
`;

const SectionHeader = styled(motion.div)`
  margin-bottom: 4rem;
  
  h2 {
    font-size: 2.5rem;
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    
    &::before {
      content: '04.';
      font-family: 'JetBrains Mono', monospace;
      font-size: 1.25rem;
      color: ${props => props.theme.colors.primary};
    }
    
    &::after {
      content: '';
      flex: 1;
      height: 1px;
      background: ${props => props.theme.colors.border};
      margin-left: 2rem;
    }
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(56, 189, 248, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(56, 189, 248, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(56, 189, 248, 0);
  }
`;

const Timeline = styled.div`
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 7px;
    width: 2px;
    height: 100%;
    background: linear-gradient(
      to bottom,
      ${props => props.theme.colors.primary},
      ${props => props.theme.colors.border}
    );
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    &::before {
      left: 0;
    }
  }
`;

const TimelineItem = styled(motion.div)`
  position: relative;
  padding-left: 3.5rem;
  padding-bottom: 4rem;
  
  &:last-child {
    padding-bottom: 0;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding-left: 2rem;
    padding-bottom: 3rem;
  }
`;

const TimelineDot = styled(motion.div)`
  position: absolute;
  left: 0;
  top: 4px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${props => props.theme.colors.surface};
  border: 3px solid ${props => props.theme.colors.primary};
  z-index: 1;
  
  &.active {
    background: ${props => props.theme.colors.primary};
    animation: ${pulse} 2s infinite;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    left: -7px;
    width: 14px;
    height: 14px;
    border-width: 2px;
  }
`;

const Period = styled.div`
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.85rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.75rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(56, 189, 248, 0.1);
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
`;

const Role = styled.h3`
  font-size: 1.4rem;
  color: ${props => props.theme.colors.text};
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const Company = styled.h4`
  font-size: 1rem;
  color: ${props => props.theme.colors.textDim};
  margin-bottom: 1.5rem;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &::before {
    content: '@';
    color: ${props => props.theme.colors.primary};
  }
`;

const Description = styled.ul`
  list-style: none;
  
  li {
    position: relative;
    padding-left: 1.5rem;
    margin-bottom: 0.75rem;
    color: ${props => props.theme.colors.textDim};
    font-size: 1rem;
    line-height: 1.7;
    
    &::before {
      content: '▹';
      position: absolute;
      left: 0;
      color: ${props => props.theme.colors.primary};
    }
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const Experience = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const experiences = [
    {
      period: "2023.12 - ing",
      role: "Business Automation - Automation Engineer",
      company: "Coupang eats services",
      active: true,
      description: [
        "Operations Web System(KR/JP) - Workspace, Dashboard 구축 생산성 70% 향상 ",
        "Assign Automation Bot(KR/JP) - Logistic Regression을 적용 배정 봇 구축 연 4-5억 절감",
        "AI Data Agent - Text-to-SQL  봇 구축으로 데이터 접근성 향상 및 업무 리드타임 80% 단축",
        "Analyzer  Agent – 데이터 수집 자동화,감정 분석 프롬프트 엔지니어링으로 자동화"
      ]
    },
    {
      period: "2024.03 - ing ",
      role: "컴퓨터 공학과",
      company: "서울 사이버 대학교",
      active: true,
      description: [
        "소프트웨어 설계 및 개발",
        "프로그래밍언어 (Python, Java, C++)",
        "모바일 앱 제작프로그래밍",
        "메타버스 프로그래밍",
        "프롬프트 엔지니어링",
        "데이터베이스 프로그래밍, 구축",
        "빅데이터 프로그래밍"
      ]
    },
    {
      period: "2023.05 - 2023.11",
      role: "빅데이터 전문가 교육 수료 (960시간)",
      company: "부산시, 한국품질재단, 정보산업진흥원 주관",
      active: false,
      description: [
        "Python 기반 데이터 분석 및 시각화 프로젝트",
        "머신러닝/딥러닝 모델링 실습 (Scikit-learn, TensorFlow)",
        "대상 프로젝트 수상 (주제: 사고 예방을 위한 실시간 행동 감지 시스템 )"
      ]
    },
    {
      period: "2013.03 - 2016.02",
      role: "컴퓨터 보안과",
      company: "부일전자디자인고",
      active: false,
      description: [
        "프로그래밍 기법 (Java, Javascript, Python)",
        "데이터 베이스 프로그래밍 (SQL)",
      ]
    }
  ];

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: {
        duration: 0.6,
        ease: [0.4, 0, 0.2, 1]
      }
    }
  };

  return (
    <Section id="experience">
      <Container>
        <SectionHeader
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2>Experience</h2>
        </SectionHeader>
        
        <Timeline
          ref={ref}
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {experiences.map((exp, index) => (
            <TimelineItem
              key={index}
              variants={itemVariants}
            >
              <TimelineDot 
                className={exp.active ? 'active' : ''}
                initial={{ scale: 0 }}
                animate={isInView ? { scale: 1 } : {}}
                transition={{ delay: index * 0.3, duration: 0.4, type: "spring" }}
              />
              <Period>{exp.period}</Period>
              <Role>{exp.role}</Role>
              <Company>{exp.company}</Company>
              <Description>
                {exp.description.map((desc, i) => (
                  <motion.li 
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: index * 0.3 + i * 0.1 + 0.3 }}
                  >
                    {desc}
                  </motion.li>
                ))}
              </Description>
            </TimelineItem>
          ))}
        </Timeline>
      </Container>
    </Section>
  );
};

export default Experience;
